import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import type { Chamber } from '@/types/raspi';

interface OffsetCalibrationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  chamber: Chamber | null;
  isCalibrating: boolean;
}

const OffsetCalibrationDialog: React.FC<OffsetCalibrationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  chamber,
  isCalibrating
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={isCalibrating ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoIcon color="info" />
        Perform Offset Calibration?
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            This will overwrite existing calibration offsets.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Process:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Uses current sensor readings to calculate new offsets</li>
              <li>All sensors will be normalized to the average reading</li>
              <li>Offsets will remain active until reboot or reset</li>
            </ul>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            ⚠️ Ensure sensors are stable before calibration.
          </Typography>
        </Alert>

        {chamber && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Chamber ID:</strong> {chamber.chamber_id}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={onClose} 
          disabled={isCalibrating}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          disabled={isCalibrating}
          variant="contained" 
          color="primary"
        >
          {isCalibrating ? 'Calibrating...' : 'Calibrate Sensors'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OffsetCalibrationDialog;

